/**
 * Deciding whether - and how - to offer "Add to Home Screen".
 *
 * ⭐⭐ THE ASYMMETRY THAT SHAPES THIS FILE: Android Chrome fires `beforeinstallprompt`, which the
 * page can capture and replay from a real button. **iOS fires nothing.** Add to Home Screen lives
 * in Safari's Share sheet and can only be described in words. So there are three outcomes, and a
 * design that waits for an event would be permanently invisible on the phone Joseph carries.
 *
 * Kept pure, and fed an explicit environment rather than reading `window` itself, so the eight
 * cases can be asserted directly instead of through a rendered component.
 */

/** What, if anything, to put in front of the user. */
export type InstallOffer =
  /** Nothing to offer: already installed, inside the native shell, or on the desktop. */
  | 'none'
  /** Chrome handed us a `beforeinstallprompt` event; the button can really install. */
  | 'event'
  /** iOS. No event exists - show the Share-sheet steps instead. */
  | 'ios-instructions';

export interface InstallEnvironment {
  userAgent: string;
  /** `matchMedia('(display-mode: standalone)').matches` - the standard signal. */
  displayModeStandalone: boolean;
  /**
   * `navigator.standalone`. WebKit-only, and `undefined` everywhere else - which is also how we
   * tell an iPad claiming to be a Mac from an actual Mac.
   */
  iosStandalone?: boolean;
  /** True inside the Capacitor WebView, where the app is already installed by definition. */
  hasNativeBridge: boolean;
  /** True once a `beforeinstallprompt` event has been captured and is still replayable. */
  deferredEvent: boolean;
  /** `navigator.maxTouchPoints`. The only reliable tell for iPadOS 13+. */
  maxTouchPoints?: number;
}

/**
 * ⚠️ iPadOS 13+ ships a DESKTOP Safari user agent - "Macintosh; Intel Mac OS X" - so the UA
 * string alone cannot see an iPad. Two tells together can: WebKit defines `navigator.standalone`
 * (so the property EXISTS, even as false) and a real Mac reports no touch points.
 */
const isIosLike = (env: InstallEnvironment): boolean => {
  if (/iPhone|iPod/i.test(env.userAgent)) return true;
  if (/iPad/i.test(env.userAgent)) return true;
  const webkitOnlyProperty = env.iosStandalone !== undefined;
  const touches = (env.maxTouchPoints ?? 0) > 1;
  return webkitOnlyProperty && touches && /Macintosh/i.test(env.userAgent);
};

/** Android phones and tablets. Chrome there is the one browser that fires the event. */
const isAndroid = (env: InstallEnvironment): boolean => /Android/i.test(env.userAgent);

export const resolveInstallOffer = (env: InstallEnvironment): InstallOffer => {
  // Already an app - by installation, or because this IS the native build.
  if (env.hasNativeBridge) return 'none';
  if (env.displayModeStandalone) return 'none';
  if (env.iosStandalone === true) return 'none';

  // iOS is checked BEFORE the event, because on iOS there will never be one.
  if (isIosLike(env)) return 'ios-instructions';

  // Everywhere else the event is the only honest signal that installing will work.
  if (isAndroid(env) && env.deferredEvent) return 'event';

  return 'none';
};
